import { StandardEvents } from '@shopify/events';

// Track del variant picker. Coordina 3 comportamientos ligados al scroll:
//
//   1. Toggle de la visibilidad del grupo de arrows (header derecho, solo
//      desktop). Aparece cuando scrollWidth > clientWidth del track.
//   2. Toggle de los gradientes de borde en el viewport (data-can-scroll-*).
//      Pistas visuales sutiles de que hay más contenido; en móvil son la
//      única señal, en desktop refuerzan las arrows.
//   3. Estado aria-disabled de cada arrow según scrollLeft (al principio o
//      al final del track).
//
// Arquitectura: cada <fieldset data-picker-fila> es una unidad. Contiene
// header (con arrows) y viewport (con track). El JS busca los controles
// por data-picker-* dentro del fila.
//
// Event delegation en document para sobrevivir al morph de Horizon
// (variant-picker.js hace fetch + morph al cambiar variante). Los data-*
// se preservan a través del morph porque el HTML re-renderizado los emite
// igual desde Liquid.

const PAGE_FRACTION = 0.85; // Cada click de arrow scrollea 85% del viewport visible.

function updateAll(fila) {
  const viewport = fila.querySelector('[data-picker-viewport]');
  const track = fila.querySelector('[data-picker-track]');
  if (!track || !viewport) return;
  const arrows = fila.querySelector('[data-picker-arrows]');
  const prev = fila.querySelector('[data-picker-prev]');
  const next = fila.querySelector('[data-picker-next]');

  const sw = track.scrollWidth;
  const cw = track.clientWidth;

  // Guard (2026-09-30): si el track aún no tiene ancho medible, estamos en
  // un estado transitorio (mid-morph tras variant swap, o mid-mount inicial).
  // Medir aquí da `scrollWidth === clientWidth` o ambos 0 → hasOverflow=false
  // → toggleAttribute('hidden', true) → arrows quedan ocultas hasta el
  // próximo evento (scroll, resize, focusin). No concluir nada: salir sin
  // tocar el estado. El caller (init cascade, productSelect handler con
  // doble rAF, ResizeObserver) volverá a medir con layout estable.
  if (cw === 0) return;

  const hasOverflow = sw - cw > 1;
  const atStart = track.scrollLeft <= 1;
  const atEnd = track.scrollLeft + cw >= sw - 1;

  // Grupo de arrows: hidden cuando no hay overflow. hidden es attribute
  // HTML5, hace display: none en cualquier navegador y no necesita CSS
  // específico.
  if (arrows) arrows.toggleAttribute('hidden', !hasOverflow);

  // Estado aria de las arrows según posición.
  if (prev) prev.setAttribute('aria-disabled', atStart ? 'true' : 'false');
  if (next) next.setAttribute('aria-disabled', atEnd ? 'true' : 'false');

  // Gradientes de borde del viewport (data-attrs que dispara CSS).
  viewport.dataset.canScrollLeft = hasOverflow && !atStart ? 'true' : 'false';
  viewport.dataset.canScrollRight = hasOverflow && !atEnd ? 'true' : 'false';
}

function scrollByPage(fila, direction) {
  const track = fila.querySelector('[data-picker-track]');
  if (!track) return;
  const delta = track.clientWidth * PAGE_FRACTION * direction;
  track.scrollBy({ left: delta, behavior: 'smooth' });
}

function initFila(fila) {
  if (fila.dataset.the90sPickerReady === '1') return;
  fila.dataset.the90sPickerReady = '1';

  const track = fila.querySelector('[data-picker-track]');
  if (!track) return;

  // Múltiples checkpoints para evitar false negatives:
  //   1. requestAnimationFrame → tras primer paint (funciona en casi todos los casos).
  //   2. setTimeout 150ms → tras carga inicial de imágenes cacheadas y fonts base.
  //   3. window.load → red de seguridad final cuando TODO cargó (imágenes de
  //      variantes, fonts webfont, layout shifts posteriores).
  //   4. ResizeObserver → cualquier cambio de tamaño del track posterior.
  //   5. scroll listener → estados de arrows/gradientes durante scroll.
  //
  // Contexto: en desktop se veían flechas ausentes con overflow real. Se
  // arregló al aplicar dos cambios a la vez — introducir estos retries y
  // quitar un `overflow: hidden` del wrapper del viewport. No se verificó
  // cuál de los dos cambios lo arregló de forma aislada.
  // Hipótesis: el falso negativo venía de medir `scrollWidth`/`clientWidth`
  // antes de que la columna 1fr del grid estabilizara su ancho (fonts,
  // imágenes de swatch, layout shifts). Los retries cubren ese caso. No
  // verificado — si en el futuro alguien duda del origen, hay que probar
  // aislando: retries sin quitar overflow, o al revés.
  requestAnimationFrame(() => updateAll(fila));
  setTimeout(() => updateAll(fila), 150);
  if (document.readyState !== 'complete') {
    window.addEventListener('load', () => updateAll(fila), { once: true });
  }

  track.addEventListener('scroll', () => updateAll(fila), { passive: true });

  if ('ResizeObserver' in window) {
    const ro = new ResizeObserver(() => updateAll(fila));
    ro.observe(track);
  }

  // Cuando el usuario navega con teclado (Tab) a un swatch, algunos
  // navegadores no lo hacen scroll-into-view. Forzamos comportamiento
  // consistente escuchando focusin del track.
  track.addEventListener('focusin', (e) => {
    const swatch = e.target && e.target.closest ? e.target.closest('.the90s-variant-picker__swatch') : null;
    if (!swatch) return;
    swatch.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  });
}

function initAll(root = document) {
  for (const fila of root.querySelectorAll('[data-picker-fila]')) initFila(fila);
}

// Delegación global de clicks en arrows. Sobrevive al morph de Horizon
// porque el listener vive en document, no en los botones.
document.addEventListener('click', (e) => {
  const target = e.target instanceof Element ? e.target : null;
  if (!target) return;
  const prev = target.closest('[data-picker-prev]');
  if (prev) {
    const fila = prev.closest('[data-picker-fila]');
    if (fila && prev.getAttribute('aria-disabled') !== 'true') scrollByPage(fila, -1);
    return;
  }
  const next = target.closest('[data-picker-next]');
  if (next) {
    const fila = next.closest('[data-picker-fila]');
    if (fila && next.getAttribute('aria-disabled') !== 'true') scrollByPage(fila, 1);
  }
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}

// Re-init tras morph de Horizon (variant-picker.js hace fetch + morph al
// cambiar variante; los data-picker-fila nuevos necesitan init).
document.addEventListener('shopify:section:load', (e) => initAll(e.target || document));

// Fallo 3 (2026-09-29 / refinado 2026-09-30): al cambiar variante, el
// variant-picker de Horizon hace morph SOLO de <variant-picker> — no dispara
// `shopify:section:load`. El HTML nuevo emite `<div data-picker-arrows hidden>`
// (Liquid no sabe si hay overflow, es decisión CSS-runtime), el morph
// reestablece ese hidden, y sin refresco las arrows quedan ocultas aunque
// haya overflow real.
//
// Fix: escuchar `productSelect` y refrescar el estado tras el morph. El
// `.then` del `event.promise` corre en el MICROTASK inmediatamente tras el
// resolve (que Horizon hace justo después del morph síncrono). En ese
// microtask el DOM ya está morfeado, PERO el navegador aún no ha recomputado
// layout — `track.scrollWidth` y `clientWidth` devuelven valores stale (o 0).
// Diagnosticado 2026-09-30 con MutationObserver: la única mutación era mi
// propio `toggleAttribute('hidden', true)` porque medía `hasOverflow=false`.
//
// Solución: doble `requestAnimationFrame` antes de llamar a `updateAll`. rAF
// #1 pasa al siguiente frame (layout recomputado). rAF #2 belt-and-suspenders
// para casos con fonts/imágenes en carga que causan un reflow extra. Sin la
// doble espera, el `guard cw === 0` de updateAll dispara en la mayoría de
// casos y las arrows nunca se activan.
document.addEventListener(StandardEvents.productSelect, (event) => {
  if (!(event.target instanceof Element)) return;
  const section = event.target.closest('.shopify-section, dialog');
  if (!section) return;
  event.promise
    .then(() => {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          for (const fila of section.querySelectorAll('[data-picker-fila]')) updateAll(fila);
        })
      );
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[the90s-variant-picker-track] productSelect rejected:', error);
    });
});
